import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Paperclip, X, FileText, Image as ImageIcon, Download } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ALLOWED = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_MB = 10;

/**
 * Inline attachment picker — used inside textarea-style inputs in the Flow timeline.
 * Props:
 *   files: File[]
 *   onChange(files: File[]): void
 *   disabled: bool
 */
export const FlowAttachInput = ({ files, onChange, disabled = false }) => {
  const inputRef = useRef(null);

  const handlePick = (e) => {
    const incoming = Array.from(e.target.files || []);
    const valid = [];
    const rejectedSize = [];
    const rejectedType = [];
    incoming.forEach((f) => {
      const okType = ALLOWED.includes(f.type) || /\.(pdf|png|jpg|jpeg|webp|gif)$/i.test(f.name);
      if (!okType) {
        rejectedType.push(f.name);
        return;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        rejectedSize.push({ name: f.name, sizeMB: (f.size / 1024 / 1024).toFixed(1) });
        return;
      }
      valid.push(f);
    });

    // Avisa o utilizador EXPLICITAMENTE sobre cada rejeição
    rejectedSize.forEach((r) => {
      toast.error(
        `Ficheiro demasiado grande: "${r.name}" (${r.sizeMB} MB). Tamanho máximo permitido: ${MAX_SIZE_MB} MB.`,
        { duration: 6000, id: `size-${r.name}` }
      );
    });
    rejectedType.forEach((name) => {
      toast.error(
        `Formato não suportado: "${name}". Aceita-se apenas PDF e imagens (PNG, JPG, WEBP, GIF).`,
        { duration: 6000, id: `type-${name}` }
      );
    });

    if (valid.length > 0) {
      onChange([...files, ...valid]);
    }
    e.target.value = '';
  };

  return (
    <>
      <input ref={inputRef} type="file" multiple accept=".pdf,image/*" onChange={handlePick} className="hidden" disabled={disabled} data-testid="flow-attach-input" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-8 w-8 rounded-full text-slate-500 hover:text-[#2D3E6F] hover:bg-slate-100"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        title="Anexar imagem ou PDF"
        data-testid="flow-attach-button"
      >
        <Paperclip className="w-4 h-4" />
      </Button>
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2 w-full">
          {files.map((f, idx) => (
            <div key={`${f.name}-${idx}`} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-100 text-xs" data-testid={`pending-attach-${idx}`}>
              {f.type.startsWith('image/') ? <ImageIcon className="w-3 h-3 text-sky-500" /> : <FileText className="w-3 h-3 text-rose-500" />}
              <span className="text-slate-700 max-w-[180px] truncate">{f.name}</span>
              <button type="button" onClick={() => onChange(files.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

/**
 * Displays attachments inline inside an activity card.
 * Props:
 *   attachments: [{ id, filename, content_type, is_image, size }]
 */
export const FlowAttachmentsView = ({ attachments }) => {
  const [preview, setPreview] = useState(null);
  if (!attachments || attachments.length === 0) return null;

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const downloadUrl = (id) => `${API_URL}/files/${id}/download${token ? `?auth=${encodeURIComponent(token)}` : ''}`;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {attachments.map(att => {
        if (att.is_image) {
          return (
            <button
              key={att.id}
              type="button"
              onClick={() => setPreview(att)}
              className="block rounded-lg overflow-hidden border border-slate-200 hover:border-[#2D3E6F]/40 transition-colors"
              data-testid={`flow-attachment-${att.id}`}
            >
              <img
                src={downloadUrl(att.id)}
                alt={att.filename}
                className="max-h-40 max-w-[240px] object-cover bg-slate-50"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </button>
          );
        }
        return (
          <a
            key={att.id}
            href={downloadUrl(att.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-100 hover:border-rose-200 text-xs"
            data-testid={`flow-attachment-${att.id}`}
          >
            <FileText className="w-4 h-4 text-rose-500" />
            <span className="text-slate-700 max-w-[180px] truncate">{att.filename}</span>
            <Download className="w-3.5 h-3.5 text-slate-400" />
          </a>
        );
      })}
      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-6" onClick={() => setPreview(null)}>
          <div className="relative max-w-5xl max-h-full">
            <button type="button" onClick={() => setPreview(null)} className="absolute -top-10 right-0 text-white hover:text-slate-200">
              <X className="w-6 h-6" />
            </button>
            <img src={downloadUrl(preview.id)} alt={preview.filename} className="max-w-full max-h-[85vh] rounded-lg" onClick={(e) => e.stopPropagation()} />
            <p className="text-white text-center mt-3 text-sm">{preview.filename}</p>
          </div>
        </div>
      )}
    </div>
  );
};
